<template>
  <v-card data-tourid="validationView-card-header">
    <div class="d-flex align-center">
      <router-link
        data-tourid="validationSection-revLink"
        class="body-1 text-decoration-none rev-title mr-4 cursor-pointer"
        :to="{
          name: revType,
          params: {
            revId,
            organizationId,
          },
        }"
        >{{ rev.name }}</router-link
      >
      <div class="d-flex ml-auto">
        <Button
          outlined
          plain
          class="d-flex justify-center align-center px-3 px-3 pr-2"
          data-testid="open-rev-offcanvas-button"
          icon="mdi-chevron-right"
          iconPosition="right"
          :text="$t('common.rev_details')"
          @click="handleShowRevDetails()"
        />
      </div>
    </div>
    <div
      v-if="
        requestedBy ||
          validatedReview.lastValidatedBy ||
          financialValidation.isValidated
      "
      class="mt-4 d-flex align-end"
    >
      <div class="mr-3 d-flex flex-column" v-if="assignee">
        <p class="caption ma-0 pa-0 mb-2 blue-grey--text text--darken-1">
          {{ $t('common.assignee_one') }}
        </p>
        <ChipAvatar :account="assignee" small />
      </div>
      <div class="mr-3 d-flex flex-column" v-if="requestedBy">
        <p class="caption ma-0 pa-0 mb-2 blue-grey--text text--darken-1">
          {{ $t('rev.validation.requested_by') }}
        </p>
        <ChipAvatar :account="requestedBy" small />
      </div>
      <div class="mr-3 d-flex flex-column" v-if="requestedOn">
        <p class="caption ma-0 pa-0 mb-2 blue-grey--text text--darken-1">
          {{ $t('rev.validation.requested_on') }}
        </p>
        <v-chip small outlined>
          <v-icon small left color="grey">
            mdi-calendar-outline
          </v-icon>
          <p class="ma-0 pa-0 text-body-2 blue-grey--text text--darken-3">
            {{ humanDate(requestedOn) }}
          </p>
        </v-chip>
      </div>
      <template v-if="financialValidation.isValidated">
        <div class="d-flex flex-column mr-3">
          <FieldLabel
            class="mb-1"
            :text="$t(`rev.validation.impact_validated_by`)"
          />
          <ChipAvatar small :account="financialValidation.validatedBy" />
        </div>
        <div class="d-flex flex-column align-center mr-3">
          <FieldLabel
            class="mb-1"
            :text="$t(`rev.validation.impact_validated_on`)"
          />
          <v-chip small outlined>
            <v-icon small left color="grey">
              mdi-calendar-outline
            </v-icon>
            <p class="ma-0 pa-0 text-body-2 blue-grey--text text--darken-3">
              {{ toHumanDistanceToNow(financialValidation.validatedAt) }}
            </p>
          </v-chip>
        </div>
      </template>
      <template v-if="validatedReview.hasUpdatedValidation">
        <div class="d-flex flex-column mr-3">
          <FieldLabel
            class="mb-1"
            :text="$t(`rev.validation.last_validated_by`)"
          />
          <ChipAvatar small :account="validatedReview.lastValidatedBy" />
        </div>
        <div class="d-flex flex-column">
          <FieldLabel
            class="mb-1"
            :text="$t(`rev.validation.last_validated_on`)"
          />
          <v-chip small outlined>
            <v-icon small left color="grey">
              mdi-calendar-outline
            </v-icon>
            <p class="ma-0 pa-0 text-body-2 blue-grey--text text--darken-3">
              {{ toHumanDistanceToNow(validatedReview.lastValidatedOn) }}
            </p>
          </v-chip>
        </div>
      </template>
    </div>

    <EntityDrawer
      :content="revDrawerContent"
      :visible="showRevDrawer"
      @close="showRevDrawer = false"
    />
  </v-card>
</template>
<script>
import _ from 'lodash'

import { utils } from 'frontend-js-shared'

import trackerMixin from '@/mixins/tracking/trackerMixin'

import ChipAvatar from '@/components/ChipAvatar.vue'
import Button from '@/components/UI/Button'
import EntityDrawer from '@/components/EntityDrawer/EntityDrawer.vue'
import FieldLabel from '@/components/Core/FieldLabel'

export default {
  name: 'FinancialValidationHeader',

  mixins: [trackerMixin],

  components: {
    ChipAvatar,
    Button,
    EntityDrawer,
    FieldLabel,
  },

  props: {
    revType: { type: String, required: true },
    revId: { type: String, required: true },
    organizationId: { type: String, required: true },
    rev: { type: Object, required: true },
    validationInProgress: Boolean,
    assignee: Object,
    requestedBy: Object,
    requestedOn: String,
  },

  data() {
    return {
      showRevDrawer: false,
    }
  },

  computed: {
    validation() {
      return this.rev?.validations ?? this.rev?.validation ?? null
    },

    financialValidation() {
      return {
        isValidated: Boolean(
          this.validation?.financial?.isValidated ?? this.validation?.financial
        ),
        validatedAt:
          this.validation?.financialValidatedAt ??
          this.validation?.financial?.validatedAt,
        validatedBy:
          this.validation?.financialValidatedBy ??
          this.validation?.financial?.auditor,
      }
    },

    validatedReview() {
      return {
        hasUpdatedValidation: Boolean(this.rev?.validatedReview?.lastUpdatedBy),
        lastValidatedOn: this.rev?.validatedReview?.updatedAt,
        lastValidatedBy: this.rev?.validatedReview?.lastUpdatedBy,
      }
    },

    revDrawerContent() {
      return {
        ...this.rev,
        entityType: this.revType,
        status: this.rev?.status ?? this.rev?.revCondition,
        owner: this.rev?.team?.owner ?? this.rev?.owner,
        coach:
          (!_.isEmpty(this.rev?.team?.coach) ? this.rev?.team.coach : null) ??
          this.rev?.coach ??
          null,
        collaborators: this.rev?.team?.collaborators ?? [],
        site: this.rev?.location?.site,
        tags: this.rev?.location?.tags ?? [],
        image:
          this.rev?.beforePhase?.media?.[0] ??
          this.rev?.problemAnalysis?.problemMedia?.[0] ??
          this.rev?.relate?.media?.[0] ??
          null,
        organization:
          this.rev?.organization ??
          this.rev?.organization?.id ??
          this.rev?.location?.organization?._id,
      }
    },
  },

  methods: {
    humanDate(date) {
      return utils.dates.humanDate(date)
    },

    toHumanDistanceToNow(date) {
      return utils.dates.toHumanDistanceToNow(date)
    },

    handleShowRevDetails() {
      this.trackValidationsActions({
        action: 'openDrawer-revDetails',
        revId: this.revId,
        revType: this.revType,
      })
      this.showRevDrawer = true
    },
  },
}
</script>
<style lang="scss" scoped>
.rev-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: $color-blue-grey-darken3;
  &:hover {
    color: $color-blue !important;
    text-decoration: underline !important;
  }
}
</style>
